import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Typography, useTheme } from '@qbe/ads-core';
import React, { useState } from 'react';

import Loading from '~/components-ds/Loading';
import Modal from '~/components-ds/Modal';
import { useAppContent, useAppDispatch, useExtraInfo } from '~/hooks';
import { removeInterestedParty, updateNeedInterestedParties } from '~/slices/extraInfo';
import { removeCover } from '~/slices/selectCover';
import { qalTrackEventAction } from '~/thunks/analytics';
import { quotePremiumsAction } from '~/thunks/quotePremiumsAction';
import { Cover } from '~/types/cover';

interface RemoveCoverModalProps {
  onCancel: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onRemove: () => void;
  covers: Cover[];
}

const RemoveCoverModal: React.FC<RemoveCoverModalProps> = ({ onCancel, onRemove, covers }) => {
  const { getTextContent } = useAppContent({ basePath: 'dsme.review.fields.removeCoverModal' });

  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { interestedParties } = useExtraInfo();
  const [isLoading, setIsLoading] = useState(false);

  const isBACAndBI = [Cover.BuildingAndContents, Cover.BusinessInterruption].every((cover) =>
    covers.includes(cover)
  );

  const onRemoveCover = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    dispatch(
      qalTrackEventAction({
        event: 'submit',
        eventAction: 'Remove Cover',
        eventLabel: covers.toString(),
      })
    );
    setIsLoading(true);
    covers?.forEach((cover) => {
      dispatch(removeCover(cover));
    });
    const partiesToRemove = interestedParties?.filter((party) => covers.includes(party.cover));
    if (partiesToRemove) {
      partiesToRemove.forEach((party) => dispatch(removeInterestedParty(party.id)));
      if (partiesToRemove.length === interestedParties!.length) {
        dispatch(updateNeedInterestedParties(false));
      }
    }
    await dispatch(quotePremiumsAction());
    setIsLoading(false);
    onRemove();
  };

  return (
    <Modal
      open
      data-testid="remove-cover-modal"
      id="remove-cover-modal"
      analyticsModalName="Remove Cover Modal"
      title={getTextContent('title')}
      iconProps={{
        muiIcon: InfoOutlined,
        // TODO - https://qbe-appservices.atlassian.net/browse/AD1-445
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        color: theme.color.icon.info,
      }}
      primaryBtnProps={{
        label: getTextContent('primaryAction'),
        disabled: isLoading,
        'data-track': 'remove',
        onClick: onRemoveCover,
      }}
      secondaryBtnProps={{
        label: getTextContent('secondaryAction'),
        'data-track': 'cancel',
        onClick: onCancel,
      }}
    >
      <>
        {isLoading && <Loading />}
        <Typography>
          {isBACAndBI ? getTextContent('bacAndBi') : getTextContent('description')}
        </Typography>
      </>
    </Modal>
  );
};

export default RemoveCoverModal;
